import React, { useMemo, useState } from "react";
import {
  Paper,
  Tabs,
  Tab,
  Divider,
  Box,
  Typography,
  Grid,
  Stack,
  Button,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import StethoscopeIcon from "@mui/icons-material/MedicalServices";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  HCCConditionsList,
  TreatmentPlan,
  RAFScore,
  RiskCodingAnalysis,
  HEDISMetricsReport,
  PreventiveServicesReport,
  ProviderEncounterTable,
  ClinicalAiPanels,
  FutureRAFScore,
} from "../../../ClinicalDataCollector/components/output";

// Panel simple
function TabPanel({ children, value, index }) {
  const isActive = value === index;
  return (
    <div role="tabpanel" hidden={!isActive}>
      <Box sx={{ pt: 2, display: isActive ? "block" : "none" }}>{children}</Box>
    </div>
  );
}

export default function AiReportsPanel({
  // context
  displayPatient,
  patientAge,
  advancedMdPatientId,

  annualWellnessNotes,
  followUpExamNotes,
  telehealthNotes,
  hospitalDischargeNotes,

  clinicalNotesForHcc,

  // RAF state/actions
  rafLoading,
  rafError,
  rafAnalysisSummary,
  rafCurrentConditions,
  rafFutureConditions,
  onGenerateRaf,

  // HCC list callback
  onHccConditionsReady,
}) {
  const [aiTab, setAiTab] = useState(0);

  const contextReady = useMemo(() => {
    const hasNotes = Boolean(clinicalNotesForHcc && clinicalNotesForHcc.trim());
    return hasNotes;
  }, [clinicalNotesForHcc]);

  return (
    <Accordion defaultExpanded sx={{ borderRadius: 3 }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack sx={{ width: "100%" }} direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography fontWeight={900}>AI Reports</Typography>
            <Typography variant="caption" color="text.secondary">
              Reports generated from the Clinical Context
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {!contextReady ? (
              <Tooltip title="Clinical context is incomplete (notes missing).">
                <Alert severity="warning" sx={{ py: 0, px: 1, borderRadius: 2 }}>
                  <Typography variant="caption">Context incomplete</Typography>
                </Alert>
              </Tooltip>
            ) : (
              <Alert severity="success" sx={{ py: 0, px: 1, borderRadius: 2 }}>
                <Typography variant="caption">Context ready</Typography>
              </Alert>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
          <Tabs
            value={aiTab}
            onChange={(_, v) => setAiTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 1 }}
          >
            <Tab icon={<FavoriteIcon />} iconPosition="start" label="Conditions & Risk" />
            <Tab icon={<LocalHospitalIcon />} iconPosition="start" label="Treatment Plan" />
            <Tab icon={<HealthAndSafetyIcon />} iconPosition="start" label="Metrics & Prevention" />
            <Tab icon={<StethoscopeIcon />} iconPosition="start" label="Specific Conditions" />
            <Tab icon={<NoteAltIcon />} iconPosition="start" label="Encounters & Notes" />
          </Tabs>

          <Divider sx={{ mb: 2 }} />

          {/* ===================== TAB 0: Conditions & Risk ===================== */}
          <TabPanel value={aiTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <HCCConditionsList
                  clinicalNotes={clinicalNotesForHcc}
                  medications={[]}
                  labs={[]}
                  onConditionsReady={onHccConditionsReady}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle1" fontWeight={900}>
                    AI Risk Adjustment (RAF)
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {rafLoading && <Typography variant="body2">Calculating…</Typography>}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={onGenerateRaf}
                      disabled={rafLoading || !contextReady}
                    >
                      Generate RAF with AI
                    </Button>

                    <Tooltip title="Uses merged notes (AWV, Follow Up, Telehealth, Discharge).">
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {rafError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <AlertTitle>RAF AI error</AlertTitle>
                    <Typography variant="body2">{rafError}</Typography>
                  </Alert>
                )}

                {rafAnalysisSummary && !rafError && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <AlertTitle>AI summary</AlertTitle>
                    <Typography variant="body2">{rafAnalysisSummary}</Typography>
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <RAFScore hccConditions={rafCurrentConditions} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FutureRAFScore
                  currentConditions={rafCurrentConditions}
                  futureConditions={rafFutureConditions}
                />
              </Grid>

              <Grid item xs={12}>
                <RiskCodingAnalysis
                  hccCurrent={rafCurrentConditions}
                  hccFuture={rafFutureConditions}
                  hccRetro={[]}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ===================== TAB 1: Treatment Plan ===================== */}
          <TabPanel value={aiTab} index={1}>
            <TreatmentPlan
              annualWellnessNotes={annualWellnessNotes || []}
              followUpExamNotes={followUpExamNotes || []}
              telehealthNotes={telehealthNotes || []}
              hospitalDischargeNotes={hospitalDischargeNotes || []}
            />
          </TabPanel>

          {/* ===================== TAB 2: Metrics & Prevention ===================== */}
          <TabPanel value={aiTab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <HEDISMetricsReport
                  patientSummary={{ ...(displayPatient || {}), age: patientAge }}
                  annualWellnessNotes={annualWellnessNotes || []}
                  followUpExamNotes={followUpExamNotes || []}
                  telehealthNotes={telehealthNotes || []}
                  hospitalDischargeNotes={hospitalDischargeNotes || []}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <PreventiveServicesReport
                  demographics={displayPatient}
                  annualWellnessNotes={annualWellnessNotes}
                  followUpExamNotes={followUpExamNotes}
                  telehealthNotes={telehealthNotes}
                  hospitalDischargeNotes={hospitalDischargeNotes}
                  medications={[]}
                  labs={[]}
                  allergies={[]}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ===================== TAB 3: Specific Conditions ===================== */}
          <TabPanel value={aiTab} index={3}>
            <ClinicalAiPanels
              patientSummary={{ ...(displayPatient || {}), age: patientAge }}
            />
          </TabPanel>

          {/* ===================== TAB 4: Encounters & Notes ===================== */}
          <TabPanel value={aiTab} index={4}>
            <ProviderEncounterTable />
          </TabPanel>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}
